import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Space, Table, Tabs, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';
import { DIVISION_NAMES } from '@/constants/divisionConstants';
import type { CohortBreakdownRow, GroupAnalyticsParams, MatchBucket } from '@/types';

const { Text } = Typography;

const BUCKET_ORDER: MatchBucket[] = ['0', '1-4', '5-19', '20-49', '50+'];

const BUCKET_LABELS: Record<MatchBucket, string> = {
  '0': '0 matches',
  '1-4': '1-4 matches',
  '5-19': '5-19 matches',
  '20-49': '20-49 matches',
  '50+': '50+ matches',
};

// Validated pair (CVD ΔE 33.8). Payers are the minority segment, so they get
// the warmer hue and sit on top of the stack.
const PAYER_DOMAIN = ['Non-payer', 'Payer'];
const PAYER_RANGE = ['#1677ff', '#fa8c16'];

interface GroupTotals {
  players: number;
  payers: number;
  custom: number;
}

interface GroupRow extends GroupTotals {
  key: string;
  label: string;
  order: number;
}

const fmt = (n: number) => n.toLocaleString('en-IN');

const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '-');

const ratio = (n: number, d: number) => (d > 0 ? n / d : 0);

const divisionLabel = (tier: number) => DIVISION_NAMES[tier] ?? `Division ${tier}`;

/** The endpoint returns one row per combination, so every group is a sum. */
const sumBy = <K extends string | number>(
  rows: CohortBreakdownRow[],
  keyOf: (r: CohortBreakdownRow) => K
): Map<K, GroupTotals> => {
  const map = new Map<K, GroupTotals>();
  for (const r of rows) {
    const k = keyOf(r);
    const t = map.get(k) ?? { players: 0, payers: 0, custom: 0 };
    t.players += r.users;
    if (r.isPayer) t.payers += r.users;
    if (r.usesCustomMatch) t.custom += r.users;
    map.set(k, t);
  }
  return map;
};

const buildColumns = (groupTitle: string, totalPlayers: number): ColumnsType<GroupRow> => [
  {
    title: groupTitle,
    dataIndex: 'label',
    key: 'label',
    sorter: (a, b) => a.order - b.order,
  },
  {
    title: 'Players',
    dataIndex: 'players',
    key: 'players',
    align: 'right',
    render: (v: number) => fmt(v),
    sorter: (a, b) => a.players - b.players,
  },
  {
    title: 'Share of all players',
    key: 'share',
    align: 'right',
    render: (_: unknown, r: GroupRow) => pct(r.players, totalPlayers),
    sorter: (a, b) => a.players - b.players,
  },
  {
    title: 'Payers',
    dataIndex: 'payers',
    key: 'payers',
    align: 'right',
    render: (v: number) => fmt(v),
    sorter: (a, b) => a.payers - b.payers,
  },
  {
    title: 'Payer share',
    key: 'payerShare',
    align: 'right',
    render: (_: unknown, r: GroupRow) => pct(r.payers, r.players),
    sorter: (a, b) => ratio(a.payers, a.players) - ratio(b.payers, b.players),
  },
  {
    title: 'Custom match players',
    dataIndex: 'custom',
    key: 'custom',
    align: 'right',
    render: (v: number) => fmt(v),
    sorter: (a, b) => a.custom - b.custom,
  },
  {
    title: 'Custom share',
    key: 'customShare',
    align: 'right',
    render: (_: unknown, r: GroupRow) => pct(r.custom, r.players),
    sorter: (a, b) => ratio(a.custom, a.players) - ratio(b.custom, b.players),
  },
];

/**
 * Player groups: who is in the population, split by how much they played, by
 * division, by whether they ever paid and by custom-match use.
 *
 * Shares are read two ways on purpose. "Share of all players" says how big a
 * group is; "Payer share" and "Custom share" are within the group, so a small
 * bucket with a high payer share stands out instead of being drowned by size.
 */
export const PlayerGroupsCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<CohortBreakdownRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCohortBreakdown(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load player groups'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () =>
      rows.reduce<GroupTotals>(
        (acc, r) => ({
          players: acc.players + r.users,
          payers: acc.payers + (r.isPayer ? r.users : 0),
          custom: acc.custom + (r.usesCustomMatch ? r.users : 0),
        }),
        { players: 0, payers: 0, custom: 0 }
      ),
    [rows]
  );

  // Only buckets the server returned — an absent bucket is not plotted as 0.
  const byBucket = useMemo<GroupRow[]>(() => {
    const map = sumBy(rows, (r) => r.matchBucket);
    return BUCKET_ORDER.flatMap((b, i) => {
      const t = map.get(b);
      return t ? [{ key: `bucket-${b}`, label: BUCKET_LABELS[b], order: i, ...t }] : [];
    });
  }, [rows]);

  const byDivision = useMemo<GroupRow[]>(
    () =>
      Array.from(sumBy(rows, (r) => r.divisionTier).entries())
        .sort((a, b) => a[0] - b[0])
        .map(([tier, t]) => ({
          key: `division-${tier}`,
          label: divisionLabel(tier),
          order: tier,
          ...t,
        })),
    [rows]
  );

  const hasZeroBucket = useMemo(() => rows.some((r) => r.matchBucket === '0'), [rows]);

  const chartData = useMemo(
    () =>
      byBucket.flatMap((r) => [
        { bucket: r.label, group: 'Non-payer', players: r.players - r.payers },
        { bucket: r.label, group: 'Payer', players: r.payers },
      ]),
    [byBucket]
  );

  const bucketColumns = useMemo(
    () => buildColumns('Matches played in range', totals.players),
    [totals.players]
  );

  const divisionColumns = useMemo(() => buildColumns('Division', totals.players), [totals.players]);

  return (
    <Card
      loading={loading}
      title={
        <span>
          Player groups{' '}
          <Tooltip title="Counts everyone active in the range plus everyone who played a match in it. The 0 matches group only fills from when daily activity tracking started. Payer means a real purchase at any time; sandbox and tester purchases are excluded. Custom match means the player played at least one custom match in the range.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {!error && rows.length === 0 && (
        <Text type="secondary">
          No players in this range. A player is counted once they have a daily activity record or a
          match in the range, and daily activity has only been recorded since activity tracking
          started.
        </Text>
      )}

      {!error && rows.length > 0 && (
        <>
          <Space size="large" wrap style={{ marginBottom: 12 }}>
            <Text>
              Players <strong>{fmt(totals.players)}</strong>
            </Text>
            <Text>
              Payers <strong>{fmt(totals.payers)}</strong>{' '}
              <Text type="secondary">({pct(totals.payers, totals.players)})</Text>
            </Text>
            <Text>
              Custom match players <strong>{fmt(totals.custom)}</strong>{' '}
              <Text type="secondary">({pct(totals.custom, totals.players)})</Text>
            </Text>
          </Space>

          <Tabs
            items={[
              {
                key: 'matches',
                label: 'By matches played',
                children: (
                  <>
                    <Column
                      data={chartData}
                      xField="bucket"
                      yField="players"
                      colorField="group"
                      stack
                      legend={{ color: { position: 'top' } }}
                      scale={{ color: { domain: PAYER_DOMAIN, range: PAYER_RANGE } }}
                      height={220}
                    />
                    <Table
                      rowKey="key"
                      size="small"
                      pagination={false}
                      dataSource={byBucket}
                      columns={bucketColumns}
                      style={{ marginTop: 12 }}
                    />
                    {!hasZeroBucket && (
                      <Text
                        type="secondary"
                        style={{ display: 'block', marginTop: 8, fontSize: 12 }}
                      >
                        No 0 matches group in this range. It only fills for days after daily
                        activity tracking started, so older ranges show players who played only.
                      </Text>
                    )}
                  </>
                ),
              },
              {
                key: 'division',
                label: 'By division',
                children: (
                  <>
                    <Table
                      rowKey="key"
                      size="small"
                      pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
                      dataSource={byDivision}
                      columns={divisionColumns}
                    />
                    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                      Players with no division record are counted in {divisionLabel(1)}.
                    </Text>
                  </>
                ),
              },
            ]}
          />
        </>
      )}
    </Card>
  );
};
